import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Tooltip } from '@mui/material';
import { faTrophy } from '@fortawesome/free-solid-svg-icons';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 600,
    maxHeight: 600,
  },
  formControl: {
    width: '100%',
  },
  fileCheckboxSection: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    maxHeight: 300,
    overflowY: 'auto',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
  },
  assignmentRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  fileInfo: {
    flex: 1,
  },
  userSelect: {
    minWidth: 200,
    marginLeft: theme.spacing(2),
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

const CoverageAssignmentModal = ({
  open,
  onClose,
  onSubmit,
  files,
  internalUsers,
  existingCoverage,
  isEditing,
  isLoading,
  currentUserName,
  selectedUserId,
  isRCRole,
}) => {
  const classes = useStyles();

  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    fileAssignments: {}, // { fileId: { userId, isRC } }
  });

  const [filters, setFilters] = useState({
    clientFilter: '',
    moveTypeFilter: '',
    vipFilter: '',
  });

  const [errors, setErrors] = useState({});

  // Initialize form data when modal opens or when editing existing coverage
  useEffect(() => {
    if (open) {
      if (isEditing && existingCoverage && existingCoverage.length > 0) {
        // Populate with existing coverage data
        const firstCoverage = existingCoverage[0];
        const assignments = {};

        existingCoverage.forEach(cov => {
          assignments[cov.fileId] = {
            userId: cov.coverageRCId || cov.coverageEAId,
            isRC: !!cov.coverageRCId,
          };
        });

        setFormData({
          startDate: firstCoverage.startDate ? firstCoverage.startDate.split('T')[0] : '',
          endDate: firstCoverage.endDate ? firstCoverage.endDate.split('T')[0] : '',
          fileAssignments: assignments,
        });
      } else {
        // Reset for new coverage
        setFormData({
          startDate: '',
          endDate: '',
          fileAssignments: {},
        });
      }
      setErrors({});
    }
  }, [open, isEditing, existingCoverage]);

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const handleFileToggle = (fileId) => {
    const newAssignments = { ...formData.fileAssignments };
    if (newAssignments[fileId]) {
      delete newAssignments[fileId];
    } else {
      newAssignments[fileId] = { userId: null, isRC: isRCRole };
    }
    setFormData({ ...formData, fileAssignments: newAssignments });
  };

  const handleUserAssignment = (fileId, userId) => {
    setFormData({
      ...formData,
      fileAssignments: {
        ...formData.fileAssignments,
        [fileId]: { ...formData.fileAssignments[fileId], userId: parseInt(userId) },
      },
    });
  };

  const handleSelectAll = () => {
    const allSelected = Object.keys(formData.fileAssignments).length === filteredFiles.length;
    if (allSelected) {
      setFormData({ ...formData, fileAssignments: {} });
    } else {
      const newAssignments = {};
      filteredFiles.forEach(file => {
        newAssignments[file.fileId] = { userId: null, isRC: isRCRole };
      });
      setFormData({ ...formData, fileAssignments: newAssignments });
    }
  };

  const handleFilterChange = (filterName, value) => {
    setFilters({ ...filters, [filterName]: value });
  };

  // Filter files based on client code (from Auth ID) and move type
  const filteredFiles = files.filter(file => {
    // Client filter: extract client code from Auth ID (e.g., "ACME" from "ACME-001")
    const clientCode = file.fileNumber?.split('-')[0] || '';
    const matchesClient = !filters.clientFilter ||
      clientCode.toLowerCase().includes(filters.clientFilter.toLowerCase());

    // Move type filter
    const matchesMoveType = !filters.moveTypeFilter ||
      file.fileTypeDescription?.toLowerCase().includes(filters.moveTypeFilter.toLowerCase());

    // VIP filter - should actively filter for VIP or not at all
    const matchesVIP = !filters.vipFilter || file.isVIP === filters.vipFilter;

    return matchesClient && matchesMoveType && matchesVIP;
  });

  // Get unique client codes and move types for filter options
  const clientCodes = [...new Set(files.map(f => f.fileNumber?.split('-')[0]).filter(Boolean))].sort();
  const moveTypes = [...new Set(files.map(f => f.fileTypeDescription).filter(Boolean))].sort();

  const validate = () => {
    const newErrors = {};

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    const selectedFileCount = Object.keys(formData.fileAssignments).length;
    if (selectedFileCount === 0) {
      newErrors.files = 'At least one file must be selected';
    }

    // Check that all selected files have a user assigned
    Object.keys(formData.fileAssignments).forEach(fileId => {
      if (!formData.fileAssignments[fileId].userId) {
        newErrors.files = 'All selected files must have a user assigned';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    // Transform data into the format expected by the API
    const coverageAssignments = Object.keys(formData.fileAssignments).map(fileId => ({
      fileId: parseInt(fileId),
      coverageUserId: formData.fileAssignments[fileId].userId,
      isRCRole: formData.fileAssignments[fileId].isRC,
    }));

    const payload = {
      startDate: formData.startDate,
      endDate: formData.endDate,
      assignedByName: currentUserName || 'Unknown User',
      createdBy: selectedUserId,
      coverageAssignments: coverageAssignments,
    };
    onSubmit(payload);
  };

  const selectedCount = Object.keys(formData.fileAssignments).length;
  const allSelected = selectedCount === filteredFiles.length && filteredFiles.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? 'Edit Coverage Assignment' : 'Assign Coverage'}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="body2" className={classes.helperText}>
          Assign coverage for your files while you are away. Selected users will be able to view and manage these files during the coverage period.
        </Typography>

        <Grid container spacing={2} style={{ marginTop: 16 }}>
          <Grid item xs={6}>
            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleFieldChange('startDate', e.target.value)}
              className={classes.formControl}
              InputLabelProps={{ shrink: true }}
              error={!!errors.startDate}
              helperText={errors.startDate}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={(e) => handleFieldChange('endDate', e.target.value)}
              className={classes.formControl}
              InputLabelProps={{ shrink: true }}
              error={!!errors.endDate}
              helperText={errors.endDate}
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" className={classes.sectionTitle}>
          Select Files and Assign Users ({selectedCount} selected)
        </Typography>

        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={4}>
            <FormControl className={classes.formControl} size="small">
              <InputLabel>Filter by Client</InputLabel>
              <Select
                value={filters.clientFilter}
                onChange={(e) => handleFilterChange('clientFilter', e.target.value)}
              >
                <MenuItem value="">
                  <em>All Clients</em>
                </MenuItem>
                {clientCodes.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl className={classes.formControl} size="small">
              <InputLabel>Filter by Move Type</InputLabel>
              <Select
                value={filters.moveTypeFilter}
                onChange={(e) => handleFilterChange('moveTypeFilter', e.target.value)}
              >
                <MenuItem value="">
                  <em>All Move Types</em>
                </MenuItem>
                {moveTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={4}>
            <FormControl className={classes.formControl} size="small">
              <InputLabel>Filter by VIP</InputLabel>
              <Select
                value={filters.vipFilter}
                onChange={(e) => handleFilterChange('vipFilter', e.target.value)}
              >
                <MenuItem value={false}>
                  <em>All</em>
                </MenuItem>
                <MenuItem value={true}>VIP Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Button
          size="small"
          onClick={handleSelectAll}
          style={{ marginBottom: 8 }}
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>

        {errors.files && (
          <Typography color="error" variant="caption" display="block">
            {errors.files}
          </Typography>
        )}

        <div className={classes.fileCheckboxSection}>
          {filteredFiles.length === 0 ? (
            <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
              No files match the selected filters.
            </Typography>
          ) : (
            filteredFiles.map((file) => {
              const isSelected = !!formData.fileAssignments[file.fileId];
              const assignedUserId = formData.fileAssignments[file.fileId]?.userId;

              return (
                <div key={file.fileId} className={classes.assignmentRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleFileToggle(file.fileId)}
                        color="primary"
                      />
                    }
                    label={
                      <div className={classes.fileInfo}>
                        <Typography variant="body2">
                          <strong>{file.fileNumber}</strong> - {file.eeFullName} - {file.fileTypeDescription || 'N/A'}
                          {file.isVIP === true && (
                            <Tooltip title="VIP File" arrow placement="right">
                              <span style={{ marginLeft: 4 }}>
                                <FontAwesomeIcon icon={faTrophy} color="gold" />
                              </span>
                            </Tooltip>
                          )}
                        </Typography>
                      </div>
                    }
                  />
                  {isSelected && (
                    <FormControl className={classes.userSelect} size="small">
                      <InputLabel>Assign to User</InputLabel>
                      <Select
                        value={assignedUserId || ''}
                        onChange={(e) => handleUserAssignment(file.fileId, e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Select User</em>
                        </MenuItem>
                        {internalUsers.map((user) => (
                          <MenuItem key={user.internalUserId} value={user.internalUserId}>
                            {user.firstName} {user.lastName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : isEditing ? 'Update Coverage' : 'Assign Coverage'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CoverageAssignmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(PropTypes.object).isRequired,
  internalUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  existingCoverage: PropTypes.array,
  isEditing: PropTypes.bool,
  isLoading: PropTypes.bool,
  currentUserName: PropTypes.string,
  // Marks new assignments as covering the RC role; expense analysts pass false
  isRCRole: PropTypes.bool,
};

CoverageAssignmentModal.defaultProps = {
  existingCoverage: null,
  isEditing: false,
  isLoading: false,
  currentUserName: 'Unknown User',
  isRCRole: true,
};

export default CoverageAssignmentModal;
